import React, { useState, useEffect } from 'react';
import { Modal, Table, InputNumber, Button, message } from 'antd';
import { useMutation } from '@apollo/client';
import { CreateBagResult } from '../graphql/mutations/createBag';
import { PRINT_SELECTED_TAGS, PrintSelectedTagsResponse, BagPrintItem } from '../graphql/mutations/batchOperations';
import { printPdfFromBase64 } from '../utils/printUtils';
import { useTranslation } from 'react-i18next';

interface PrintTagsModalProps {
    visible: boolean;
    onClose: () => void;
    bags: CreateBagResult['bags'];
    initialPrintCopies?: number;
    // bagId → 当前所属批次 id（按操作仓）：列表打印透传它，让标签批次段=列表显示的批次（跨仓时也一致）。
    // 不传则后端回退物理当前批次(is_current)。
    batchIdByBagId?: Record<string, string>;
}

const PrintTagsModal: React.FC<PrintTagsModalProps> = ({ visible, onClose, bags, initialPrintCopies = 1, batchIdByBagId }) => {
    const [printCopies, setPrintCopies] = useState<{ [key: string]: number }>({});
    const [printSelectedTags, { loading: printing }] = useMutation<PrintSelectedTagsResponse>(PRINT_SELECTED_TAGS);
    const { t } = useTranslation();
    useEffect(() => {
        if (visible) {
            const initialCopies = bags.reduce((acc, bag) => {
                acc[bag.id] = initialPrintCopies; // Use the passed initial print copies
                return acc;
            }, {} as { [key: string]: number });
            setPrintCopies(initialCopies);
        }
    }, [visible, bags, initialPrintCopies]);

    const handleCopiesChange = (bagId: string, copies: number | null) => {
        if (copies !== null) {
            setPrintCopies(prev => ({ ...prev, [bagId]: copies }));
        }
    };

    const handlePrint = async () => {
        const items: BagPrintItem[] = Object.entries(printCopies)
            .map(([bagId, copies]) => ({ bagId, copies, batchId: batchIdByBagId?.[bagId] }));

        if (items.length === 0) {
            message.warning(t('wms.bags.messages.noBagsToPrint'));
            return;
        }

        try {
            // 获取浏览器本地时间和时区
            const now = new Date();
            // 格式化为本地时间的ISO格式（如：2024-11-04T10:30:00）
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const localTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // 如：America/New_York
            
            const result = await printSelectedTags({ 
                variables: { 
                    items,
                    localTime,
                    timeZone
                } 
            });
            if (result.data?.printSelectedTags?.success && result.data.printSelectedTags.pdfBase64) {
                printPdfFromBase64(result.data.printSelectedTags.pdfBase64, `bag_tags_${Date.now()}.pdf`);
                message.success(t('wms.bags.messages.labelsGeneratedSuccessfully'));
                onClose();
            } else {
                message.error(result.data?.printSelectedTags?.message || t('wms.bags.messages.failedToGenerateLabels'));
            }
        } catch (error) {
            console.error('Printing failed:', error);
            message.error(t('wms.bags.messages.failedToCreateTags'));
        }
    };

    const columns = [
        {
            title: t('wms.bags.pageDetails.bagNumber'),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
        },
        {
            title: t('wms.bags.pageDetails.sortingCode'),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
        },
        {
            title: t('wms.bags.pageDetails.printCopies'),
            key: 'copies',
            render: (_: any, record: CreateBagResult['bags'][0]) => (
                <InputNumber
                    min={1}
                    value={printCopies[record.id] || 1}
                    onChange={(value) => handleCopiesChange(record.id, value)}
                />
            ),
        },
    ];

    return (
        <Modal
            title={t('wms.bags.pageDetails.printBagTags')}
            visible={visible}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    {t('components.button.cancel')}
                </Button>,
                <Button key="submit" type="primary" loading={printing} onClick={handlePrint}>
                    {t('components.button.print')}
                </Button>,
            ]}
            width={800}
        >
            <Table
                dataSource={bags}
                columns={columns}
                rowKey="id"
                pagination={false}
             className="mb-4"/>
        </Modal>
    );
};

export default PrintTagsModal;